import { useCallback, useEffect, useState } from 'react';

import { DefaultValues, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentUser, USER_QUERY_KEYS } from '@/entities/user/currentUser';

import { USER_API } from '@/shared/api/user';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';

import { postToGoogleForm } from '../lib/googleForm';
import {
  RECORDING_FEEDBACK_CHAT_OPTIONS,
  RecordingFeedbackBonusForm,
  RecordingFeedbackBonusFormSchema,
} from '../lib/validationSchema';
import { useRecordingFeedbackBonusModalStore } from './useRecordingFeedbackBonusModalStore';

const DEFAULT_VALUES: DefaultValues<RecordingFeedbackBonusForm> = {
  roleOther: '',
  tools: [],
  toolsOther: '',
  useCaseOther: '',
  website: '',
};

// Mirrors the visible step order in the modal. Each entry's `fields` lists the form
// values to validate before advancing; an empty array means no validation gate.
type StepFields = (keyof RecordingFeedbackBonusForm)[];

export const STEPS: readonly { fields: StepFields; key: string }[] = [
  { fields: ['role', 'roleOther'], key: 'role' },
  { fields: ['companySize'], key: 'companySize' },
  { fields: [], key: 'website' },
  { fields: ['useCase', 'useCaseOther'], key: 'useCase' },
  { fields: ['tools', 'toolsOther'], key: 'tools' },
  { fields: ['chatWillingness'], key: 'chatWillingness' },
] as const;

export const TOTAL_STEPS = STEPS.length;

const isAlreadyClaimedError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'status' in error && (error as { status: unknown }).status === 409;

const REQUIRED = 'Field is required';

const isBlank = (value: string) => !value.trim();

type SubmissionResult = 'calendly' | 'thanks' | null;

export const useRecordingFeedbackBonusForm = () => {
  const onClose = useRecordingFeedbackBonusModalStore((state) => state.close);
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  const [stepIndex, setStepIndex] = useState(0);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult>(null);

  const { isPending, mutateAsync: claim } = useMutation({
    mutationFn: USER_API.claimRecordingFeedbackBonus,
    mutationKey: ['claimRecordingFeedbackBonus'],
  });

  const {
    clearErrors,
    control,
    formState: { isSubmitting },
    register,
    trigger,
    watch,
    getValues,
    handleSubmit,
    reset,
    setError,
  } = useForm<RecordingFeedbackBonusForm>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onSubmit',
    resolver: zodResolver(RecordingFeedbackBonusFormSchema),
  });

  const role = watch('role');
  const roleOther = watch('roleOther');
  const tools = watch('tools');
  const toolsOther = watch('toolsOther');
  const useCase = watch('useCase');
  const useCaseOther = watch('useCaseOther');

  useEffect(() => {
    if (role !== '__other_option__' || !isBlank(roleOther)) {
      clearErrors('roleOther');
    }
  }, [clearErrors, role, roleOther]);

  useEffect(() => {
    if (!tools?.includes('__other_option__') || !isBlank(toolsOther)) {
      clearErrors('toolsOther');
    }
  }, [clearErrors, tools, toolsOther]);

  useEffect(() => {
    if (useCase !== '__other_option__' || !isBlank(useCaseOther)) {
      clearErrors('useCaseOther');
    }
  }, [clearErrors, useCase, useCaseOther]);

  const resetAndClose = useCallback(() => {
    clearErrors();
    reset(DEFAULT_VALUES);
    setStepIndex(0);
    setSubmissionResult(null);
    onClose();
  }, [clearErrors, onClose, reset]);

  const refreshAndClose = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userData });
    resetAndClose();
  }, [queryClient, resetAndClose]);

  const goBack = useCallback(() => {
    clearErrors(STEPS[stepIndex].fields);
    setStepIndex((index) => Math.max(0, index - 1));
  }, [clearErrors, stepIndex]);

  const goNext = useCallback(async () => {
    const { fields, key } = STEPS[stepIndex];
    const valid = fields.length === 0 ? true : await trigger(fields);
    const values = getValues();
    let isOtherCompanionValid = true;

    clearErrors(['roleOther', 'toolsOther', 'useCaseOther']);
    if (key === 'role' && values.role === '__other_option__' && isBlank(values.roleOther)) {
      setError('roleOther', { message: REQUIRED, type: 'manual' });
      isOtherCompanionValid = false;
    }
    if (key === 'useCase' && values.useCase === '__other_option__' && isBlank(values.useCaseOther)) {
      setError('useCaseOther', { message: REQUIRED, type: 'manual' });
      isOtherCompanionValid = false;
    }
    if (key === 'tools' && values.tools?.includes('__other_option__') && isBlank(values.toolsOther)) {
      setError('toolsOther', { message: REQUIRED, type: 'manual' });
      isOtherCompanionValid = false;
    }

    if (valid && isOtherCompanionValid) {
      setStepIndex((index) => Math.min(TOTAL_STEPS - 1, index + 1));
    }
  }, [clearErrors, getValues, setError, stepIndex, trigger]);

  const onSubmit = async (values: RecordingFeedbackBonusForm) => {
    try {
      await claim();
    } catch (error) {
      if (isAlreadyClaimedError(error)) {
        await refreshAndClose();
        return;
      }
      showErrorAlert(error);
      return;
    }

    if (user?.email) {
      void postToGoogleForm(user.email, values);
    }
    await queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userData });
    setSubmissionResult(values.chatWillingness === RECORDING_FEEDBACK_CHAT_OPTIONS[0] ? 'calendly' : 'thanks');
  };

  return {
    clearErrors,
    control,
    goBack,
    goNext,
    isLastStep: stepIndex === TOTAL_STEPS - 1,
    isSubmitting: isSubmitting || isPending,
    register,
    stepIndex,
    submissionResult,
    totalSteps: TOTAL_STEPS,
    watch,
    handleSubmit: handleSubmit(onSubmit),
    onClose: resetAndClose,
  };
};
